import { useParams, Link, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { publicApi } from "../lib/api";
import { travelTips } from "../data/travelTips";
import { luxuryUpgrades, hotelCategories } from "../data/upgrades";
import { ArrowUpRight, ChevronDown, MapPin, Calendar, Star, Check, Plus } from "lucide-react";
import QuoteForm from "../components/QuoteForm";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/destinations/${slug}`)
      .then((r) => {
        setD(r.data);
        return publicApi.get("/destinations");
      })
      .then((r) => {
        const all = r.data;
        setRelated(all.filter((x) => x.slug !== slug && x.category === d?.category).slice(0, 3));
      })
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (d) {
      publicApi.get("/destinations").then((r) => {
        setRelated(r.data.filter((x) => x.slug !== d.slug && x.category === d.category).slice(0, 3));
      }).catch(() => {});
    }
  }, [d]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="font-serif text-2xl text-foreground/50">Loading…</div></div>;
  if (!d) return <Navigate to="/destinations" replace />;

  const tips = travelTips[d.slug] || [];
  const upgrades = [...(luxuryUpgrades[d.slug] || []), ...luxuryUpgrades.default].slice(0, 6);

  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: `${d.name} Luxury Journey`,
    description: d.overview,
    image: d.hero,
    touristType: "Luxury",
    provider: { "@type": "TravelAgency", name: "Voyage Luxe", url: "https://voyageluxe.com" },
    offers: { "@type": "Offer", priceCurrency: "USD", price: d.price_from.replace(/[$,]/g, ""), availability: "https://schema.org/InStock" },
  };

  return (
    <div>
      <Seo
        title={`${d.name} Luxury Tours — From ${d.price_from} · Voyage Luxe`}
        description={`${d.tagline}. ${d.short} ${d.nights} nights. Private bespoke itineraries crafted by Voyage Luxe.`}
        image={d.hero}
        type="article"
        schema={schema}
      />
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden" data-testid="dest-hero">
        <img src={d.hero} alt={d.name} className="absolute inset-0 h-full w-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/65" />
        <div className="container-luxe relative h-full flex flex-col justify-end pb-24 md:pb-32 text-white">
          <Reveal>
            <div className="eyebrow text-white/70 mb-4">{d.region} · From {d.price_from} · {d.nights} Nights</div>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="font-serif text-[clamp(3rem,9vw,8rem)] leading-[0.92] tracking-tight">{d.name}</h1>
          </Reveal>
          <Reveal delay={300}>
            <p className="mt-4 italic font-serif text-2xl md:text-3xl text-white/85">{d.tagline}</p>
          </Reveal>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="container-luxe py-6 text-[11px] uppercase tracking-[0.25em] text-foreground/50 flex items-center gap-3">
        <Link to="/" className="hover:text-accent">Home</Link>
        <span>·</span>
        <Link to="/destinations" className="hover:text-accent">Destinations</Link>
        <span>·</span>
        <span className="text-foreground">{d.name}</span>
      </nav>

      {/* Overview */}
      <section className="py-16 md:py-24 container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-7">
            <div className="eyebrow mb-4">Overview</div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight text-balance">
              {d.short}
            </h2>
            <p className="mt-8 text-foreground/75 leading-relaxed text-lg">{d.overview}</p>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:pt-16" delay={150}>
            <div className="border-t border-border">
              {[
                { l: "Best Time", v: d.best_time, icon: Calendar },
                { l: "Recommended", v: `${d.nights} Nights`, icon: Star },
                { l: "Region", v: d.region, icon: MapPin },
                { l: "From", v: d.price_from, icon: null },
              ].map((row) => (
                <div key={row.l} className="flex items-center justify-between py-5 border-b border-border">
                  <span className="eyebrow text-foreground/55">{row.l}</span>
                  <span className="font-serif text-xl">{row.v}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-foreground text-background hover:bg-accent transition-colors duration-300 uppercase text-[11px] tracking-[0.3em]" data-testid="dest-inquire-cta">
              Inquire about {d.name}
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 md:py-24 bg-secondary/40">
        <div className="container-luxe">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="eyebrow mb-3">Curated Inclusions</div>
                <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Highlights</h2>
              </div>
            </div>
          </Reveal>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
            {d.highlights.map((h, i) => (
              <Reveal key={i} delay={i * 60}>
                <li className="flex items-start gap-5 py-5 border-b border-border">
                  <span className="font-serif text-xl text-accent w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-foreground/85 text-lg leading-snug pt-0.5">{h}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Itinerary Timeline */}
      <section className="py-16 md:py-24 container-luxe" data-testid="itinerary">
        <Reveal>
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="eyebrow mb-4">Sample Itinerary</div>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">A journey, day by day.</h2>
            <p className="mt-4 text-foreground/65 text-sm leading-relaxed">An indicative outline — every itinerary is shaped around your party.</p>
          </div>
        </Reveal>

        <div className="relative max-w-4xl mx-auto">
          <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2" aria-hidden="true" />
          <ul className="space-y-12">
            {d.itinerary.map((day, i) => (
              <Reveal key={day.day} delay={i * 80}>
                <li className={`relative md:grid md:grid-cols-2 md:gap-12 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
                  <div className="pl-12 md:pl-0 md:text-right md:pr-12 relative">
                    <span className="absolute left-0 md:left-auto md:right-[-10px] top-1.5 h-5 w-5 rounded-full bg-background border-2 border-accent md:translate-x-[5px]" />
                    <div className="eyebrow text-accent">Day {String(day.day).padStart(2, "0")}</div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">{day.title}</h3>
                  </div>
                  <div className="pl-12 md:pl-12 mt-2 md:mt-0 text-foreground/70 leading-relaxed">
                    {day.body}
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Hotels */}
      <section className="py-16 md:py-24 bg-secondary/40">
        <div className="container-luxe">
          <Reveal>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className="eyebrow mb-4">Where You'll Sleep</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Hotel Partners</h2>
              <p className="mt-4 text-foreground/65 text-sm">Four categories of accommodation, hand-curated by our atelier — every property visited in person.</p>
            </div>
          </Reveal>

          {/* Hotel category chips */}
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
              {hotelCategories.map((c) => (
                <div key={c.id} className="border border-border bg-background p-5">
                  <div className="font-serif text-lg leading-tight text-accent">{c.label}</div>
                  <p className="text-[12px] text-foreground/65 mt-2 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {d.hotels.map((h, i) => (
              <Reveal key={h.name} delay={i * 100}>
                <article className="group bg-background border border-border">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={h.img} alt={h.name} className="h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="eyebrow text-foreground/55 mb-2">{h.brand}</div>
                    <h3 className="font-serif text-2xl leading-tight">{h.name}</h3>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury Upgrades */}
      {upgrades.length > 0 && (
        <section className="py-16 md:py-24 container-luxe" data-testid="luxury-upgrades">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-12">
              <div className="lg:col-span-5">
                <div className="eyebrow mb-4">Bespoke Add-Ons</div>
                <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight text-balance">
                  Luxury <span className="italic text-accent">upgrades</span>.
                </h2>
              </div>
              <p className="lg:col-span-7 text-foreground/70 leading-relaxed lg:pt-4 max-w-xl">
                Optional modules to elevate this journey from extraordinary to legendary. Discuss with your concierge — every guest's wishlist is different.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
            {upgrades.map((u, i) => (
              <Reveal key={u.title} delay={i * 70}>
                <div className="bg-background p-6 md:p-8 h-full flex items-start gap-5">
                  <Plus size={20} strokeWidth={1.25} className="text-accent shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h4 className="font-serif text-xl md:text-2xl leading-tight">{u.title}</h4>
                      <span className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 shrink-0">{u.from}</span>
                    </div>
                    <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{u.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Gallery */}
      {d.gallery?.length > 0 && (
        <section className="py-16 md:py-24 container-luxe">
          <Reveal>
            <div className="text-center mb-12">
              <div className="eyebrow mb-4">Gallery</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">A glimpse.</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {d.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className={`overflow-hidden ${i === 0 ? "row-span-2 aspect-square md:aspect-[3/4]" : "aspect-square"}`}>
                  <img src={g} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-1000" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Travel Tips */}
      {tips.length > 0 && (
        <section className="py-16 md:py-24 container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <Reveal className="lg:col-span-4">
              <div className="eyebrow mb-4">Concierge Notes</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight text-balance">
                Travel tips, from <span className="italic">people on the ground</span>.
              </h2>
              <p className="mt-6 text-foreground/65 leading-relaxed max-w-sm">
                Five gentle observations from our {d.name} atelier team — quiet wisdom collected over a decade of hosting.
              </p>
            </Reveal>
            <ul className="lg:col-span-8 space-y-1">
              {tips.map((tip, i) => (
                <Reveal key={i} delay={i * 60}>
                  <li className="flex items-start gap-4 py-5 border-b border-border">
                    <Check size={18} strokeWidth={1.5} className="text-accent shrink-0 mt-1" />
                    <span className="text-foreground/85 leading-relaxed">{tip}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-secondary/40">
        <div className="container-luxe max-w-3xl">
          <Reveal>
            <div className="text-center mb-12">
              <div className="eyebrow mb-4">Frequently Asked</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Questions, considered.</h2>
            </div>
          </Reveal>
          <ul className="border-t border-border">
            {d.faq.map((f, i) => (
              <li key={i} className="border-b border-border">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start justify-between gap-6 py-6 text-left"
                >
                  <span className="font-serif text-xl md:text-2xl">{f.q}</span>
                  <ChevronDown size={18} className={`mt-2 shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180 text-accent" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-500 ${openFaq === i ? "max-h-40 pb-6" : "max-h-0"}`}>
                  <p className="text-foreground/70 leading-relaxed">{f.a}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Inquiry CTA */}
      <section className="py-24 md:py-32 container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <div className="eyebrow mb-4">Begin Your Journey</div>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight text-balance">
              Plan your <span className="italic">{d.name}</span> journey.
            </h2>
            <p className="mt-6 text-foreground/70 leading-relaxed">
              Share your dates and dreams. Our {d.name} concierge will compose a private proposal within 24 hours.
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <QuoteForm defaultDestination={d.name} />
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-secondary/40">
          <div className="container-luxe">
            <Reveal>
              <div className="flex items-end justify-between mb-10">
                <h2 className="font-serif text-2xl md:text-3xl">You may also consider</h2>
                <Link to="/destinations" className="eyebrow hover:text-accent">All destinations →</Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 80}>
                  <Link to={`/destinations/${r.slug}`} className="group block">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={r.hero} alt={r.name} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    </div>
                    <h3 className="mt-4 font-serif text-2xl group-hover:text-accent transition-colors">{r.name}</h3>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 mt-1">From {r.price_from}</div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
